import Add from '@mui/icons-material/Add'
import Delete from '@mui/icons-material/Delete'
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'

const REPORT_TYPES = [
  'PRESCRIPTION',
  'THERAPY_REPORT',
  'SCREENING_REPORT',
  'ASSESSMENT_REPORT',
  'INVESTIGATION_REPORT',
]

function emptyRole() {
  return {
    managerId: '',
    managerEmailId: '',
    roleId: 'bookingManager',
    allowedUploadTypes: [],
    allowedPrintTypes: [],
    accessPatientDetails: false,
    accessPatientContact: false,
    accessPatientHistoryNotes: false,
    managedEvents: [],
  }
}

function cloneRoles(roles) {
  return (roles || []).map((r) => ({
    managerId: r.managerId || '',
    managerEmailId: r.managerEmailId || '',
    roleId: r.roleId || 'bookingManager',
    allowedUploadTypes: [...(r.allowedUploadTypes || [])],
    allowedPrintTypes: [...(r.allowedPrintTypes || [])],
    accessPatientDetails: Boolean(r.accessPatientDetails),
    accessPatientContact: Boolean(r.accessPatientContact),
    accessPatientHistoryNotes: Boolean(r.accessPatientHistoryNotes),
    managedEvents: [...(r.managedEvents || [])],
    startDate: r.startDate,
  }))
}

/**
 * Edit the full supportRoles[] array of a clinic assistant assignment. Each support role binds a
 * manager (picked from the brand's available managers) to a set of allowed report upload/print
 * types and patient-access flags. Saving replaces the whole array via PATCH /assignment/support-roles.
 */
export default function SupportRoleEditor({ open, supportRoles, availableManagers = [], onClose, onSave }) {
  const [roles, setRoles] = useState([])
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (open) {
      setRoles(cloneRoles(supportRoles))
    }
  }, [open, supportRoles])

  function updateRole(idx, patch) {
    setRoles((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)))
  }

  function onPickManager(idx, managerId) {
    const mgr = availableManagers.find((m) => m.managerId === managerId)
    updateRole(idx, {
      managerId,
      managerEmailId: mgr?.email || roles[idx]?.managerEmailId || '',
    })
  }

  async function handleSave() {
    const cleaned = roles.map((r) => ({
      ...r,
      managerId: (r.managerId || '').trim(),
      managedEvents: (r.managedEvents || []).map((e) => e.trim()).filter(Boolean),
    }))
    if (cleaned.some((r) => !r.managerId)) {
      toast.error('Every support role needs a manager')
      return
    }
    setSaving(true)
    const ok = await onSave(cleaned)
    setSaving(false)
    if (!ok) return
  }

  function managerLabel(m) {
    const full = `${m.firstName || ''} ${m.lastName || ''}`.trim()
    const name = full || m.userName || m.email || m.managerId
    return m.userName && name !== m.userName ? `${name} (@${m.userName})` : name
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Edit managers & support roles</DialogTitle>
      <DialogContent dividers>
        {roles.length === 0 && (
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
            No support roles yet. Add one to bind a manager and permissions.
          </Typography>
        )}
        <Stack spacing={2}>
          {roles.map((role, idx) => {
            const knownManager = availableManagers.some((m) => m.managerId === role.managerId)
            return (
              <Box key={idx} sx={{ border: '1px solid #e2e8f0', borderRadius: 1.5, p: 1.5 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                  <Typography variant="subtitle2" fontWeight={700}>
                    Support role {idx + 1}
                  </Typography>
                  <IconButton size="small" color="error" onClick={() => setRoles((p) => p.filter((_, i) => i !== idx))}>
                    <Delete fontSize="small" />
                  </IconButton>
                </Stack>

                <Stack spacing={1.5}>
                  <FormControl size="small" fullWidth>
                    <InputLabel>Manager</InputLabel>
                    <Select
                      label="Manager"
                      value={knownManager ? role.managerId : ''}
                      onChange={(e) => onPickManager(idx, e.target.value)}
                      displayEmpty
                      renderValue={(val) => {
                        if (val) {
                          const m = availableManagers.find((x) => x.managerId === val)
                          return m ? managerLabel(m) : val
                        }
                        return role.managerId ? `Current: ${role.managerEmailId || role.managerId}` : 'Select a manager'
                      }}
                    >
                      {availableManagers.length === 0 && (
                        <MenuItem disabled value="">
                          No approved managers for this brand
                        </MenuItem>
                      )}
                      {availableManagers.map((m) => (
                        <MenuItem key={m.managerId} value={m.managerId}>
                          {managerLabel(m)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {/* Manual override for managerId not in the picker list */}
                  <TextField
                    size="small"
                    label="Manager ID"
                    value={role.managerId}
                    onChange={(e) => updateRole(idx, { managerId: e.target.value })}
                    helperText={knownManager ? ' ' : 'Manager not in the brand list — editing raw ID'}
                  />

                  <TextField
                    size="small"
                    label="Role ID"
                    value={role.roleId}
                    onChange={(e) => updateRole(idx, { roleId: e.target.value })}
                  />

                  <FormControl size="small" fullWidth>
                    <InputLabel>Allowed upload types</InputLabel>
                    <Select
                      multiple
                      label="Allowed upload types"
                      value={role.allowedUploadTypes}
                      onChange={(e) => updateRole(idx, { allowedUploadTypes: e.target.value })}
                      input={<OutlinedInput label="Allowed upload types" />}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((v) => (
                            <Chip key={v} size="small" label={v} />
                          ))}
                        </Box>
                      )}
                    >
                      {REPORT_TYPES.map((t) => (
                        <MenuItem key={t} value={t}>
                          <Checkbox checked={role.allowedUploadTypes.indexOf(t) > -1} />
                          <ListItemText primary={t} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl size="small" fullWidth>
                    <InputLabel>Allowed print types</InputLabel>
                    <Select
                      multiple
                      label="Allowed print types"
                      value={role.allowedPrintTypes}
                      onChange={(e) => updateRole(idx, { allowedPrintTypes: e.target.value })}
                      input={<OutlinedInput label="Allowed print types" />}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((v) => (
                            <Chip key={v} size="small" label={v} />
                          ))}
                        </Box>
                      )}
                    >
                      {REPORT_TYPES.map((t) => (
                        <MenuItem key={t} value={t}>
                          <Checkbox checked={role.allowedPrintTypes.indexOf(t) > -1} />
                          <ListItemText primary={t} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <TextField
                    size="small"
                    label="Managed events (comma-separated IDs)"
                    value={(role.managedEvents || []).join(', ')}
                    onChange={(e) =>
                      updateRole(idx, { managedEvents: e.target.value.split(',').map((s) => s.trim()) })
                    }
                  />

                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {[
                      ['accessPatientDetails', 'Patient details'],
                      ['accessPatientContact', 'Patient contact'],
                      ['accessPatientHistoryNotes', 'History notes'],
                    ].map(([key, label]) => (
                      <FormControlLabel
                        key={key}
                        control={
                          <Checkbox
                            size="small"
                            checked={Boolean(role[key])}
                            onChange={(e) => updateRole(idx, { [key]: e.target.checked })}
                          />
                        }
                        label={<Typography variant="caption">{label}</Typography>}
                      />
                    ))}
                  </Stack>
                </Stack>
              </Box>
            )
          })}
        </Stack>
        <Divider sx={{ my: 2 }} />
        <Button startIcon={<Add />} onClick={() => setRoles((p) => [...p, emptyRole()])}>
          Add support role
        </Button>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save support roles'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
